import React, { useEffect, useMemo, useState } from "react";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlaylistStore } from "../stores/playlistStore";
import { Track } from "../types";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";

interface CreateRadioDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateStation: (name: string, seedTracks: Track[]) => void;
}

export const CreateRadioDialog: React.FC<CreateRadioDialogProps> = ({ isOpen, onClose, onCreateStation }) => {
  const [name, setName] = useState("");
  const [selectedTracks, setSelectedTracks] = useState<Track[]>([]);
  const { getAllAlbums } = useLocalLibraryStore();
  const { files } = useLocalFilesStore();
  const { playlists } = usePlaylistStore();

  const allTracks = useMemo(() => {
    const tracks: Track[] = [];
    const addedTrackIds = new Set<string>();

    // Get tracks from local files
    files.forEach((file) => {
      if (!addedTrackIds.has(file.id)) {
        tracks.push(file);
        addedTrackIds.add(file.id);
      }
    });

    // Get tracks from albums
    getAllAlbums().forEach((album) => {
      album.tracks.forEach((track) => {
        if (!addedTrackIds.has(track.id)) {
          tracks.push({
            ...track,
            source: "local",
            album: album.name,
          });
          addedTrackIds.add(track.id);
        }
      });
    });

    // Get tracks from playlists
    playlists.forEach((playlist) => {
      playlist.tracks.forEach((track) => {
        if (!addedTrackIds.has(track.id)) {
          tracks.push(track);
          addedTrackIds.add(track.id);
        }
      });
    });

    return tracks;
  }, [getAllAlbums, files, playlists]);

  useEffect(() => {
    if (selectedTracks.length > 0 && !name.trim()) {
      setName(`${selectedTracks[0].title} Radio`);
    } else if (selectedTracks.length === 0) {
      setName("");
    }
  }, [selectedTracks]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTracks.length === 0) return;

    onCreateStation(name.trim() || `${selectedTracks[0].title} Radio`, selectedTracks);
    onClose();
    setName("");
    setSelectedTracks([]);
  };

  const toggleTrackSelection = (track: Track) => {
    setSelectedTracks((prev) =>
      prev.some((t) => t.id === track.id) ? prev.filter((t) => t.id !== track.id) : [...prev, track]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle>Create Radio Station</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Station Name
            </label>
            <Input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-800"
              placeholder={selectedTracks.length > 0 ? `${selectedTracks[0].title} Radio` : "My Radio Station"}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Select Foundation Tracks (at least one)</label>
            <ScrollArea className="h-60 bg-zinc-800 rounded-md p-2">
              {allTracks.map((track) => (
                <div
                  key={track.id}
                  className={`p-2 cursor-pointer rounded ${
                    selectedTracks.some((t) => t.id === track.id) ? "bg-yellow-500 text-white" : "hover:bg-zinc-700"
                  }`}
                  onClick={() => toggleTrackSelection(track)}
                >
                  {track.title} - {track.artist}
                </div>
              ))}
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={selectedTracks.length === 0}
              className="bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
